import { Children, useDeferredValue, useEffect, useState } from "react";
import { AddIcon, CheckListIcon } from "allIcons";
import {
  BodyContainer,
  HeaderContainer,
  InputField,
  ListCard,
  ListIconWrapper,
  MainButton,
  PrimaryHeading,
  RightMainContainer,
  PopOver,
  AddFromTemplateLibrary,
  LoadingListCards,
  TemplateNotFound,
  Loader,
  FiltersList,
} from "commonUI";
import {
  useCheckListTemplates,
  useChecklistDefaultTemplates,
  useAddChecklistToDefaultTemplate,
  useRemoveChecklistDefault,
} from "api/templateLibrary/checklists";
import { isEqual, parse, search } from "helpers";
import {
  libraryItemFiltering,
  libraryItemSorting,
} from "filterAndSort/templateLibrary";
import { useTags } from "api/siteSettings/Tags";
import { useUserPermissions } from "hooks/user";
import { t } from "i18next";
import { ModulePrefixes } from "enums";

export default function Checklist() {
  const { data: DefaultChecklists, isLoading: loadingDefaultChecklists } =
    useChecklistDefaultTemplates();
  const { data: checklist, isLoading: loadingChecklistTemplate } =
    useCheckListTemplates();
  const { mutate: addToDefaultChecklist, isLoading: addingDefaultChecklist } =
    useAddChecklistToDefaultTemplate();
  const {
    mutate: removeFromDefaultChecklist,
    isLoading: removingDefaultChecklist,
  } = useRemoveChecklistDefault();
  const [addPopup, setAddPopup] = useState(false);
  const { data: tags } = useTags();

  const [searchText, setSearchText] = useState("");
  const defferedSearch = useDeferredValue(searchText);
  const [searchResult, setSearchResult] = useState([]);
  const [filterData, setFilterData] = useState({});
  const {
    remove_items_from_site_template,
    edit_templates
  } = useUserPermissions();

  useEffect(() => {
    setSearchResult(
      DefaultChecklists.filter((checklist) =>
        search(checklist.title, defferedSearch)
      )
    );
  }, [loadingDefaultChecklists, DefaultChecklists, defferedSearch]);

  const applySorting = (order, orderBy) => {
    setSearchResult((prevSearchResult) =>
      libraryItemSorting(order, orderBy, prevSearchResult)
    );
  };

  const applyFilter = ({
    selectedFilter,
    selectedTags,
    selectedLibrarySource,
    ...data
  }) => {
    setFilterData({
      ...data,
      selectedTags,
      selectedLibrarySource,
      selectedFilter,
    });
    setSearchResult(
      libraryItemFiltering(
        selectedFilter,
        selectedTags,
        selectedLibrarySource,
        DefaultChecklists
      )
    );
  };

  return (
    <>
      <RightMainContainer>
        <Loader
          show={addingDefaultChecklist || removingDefaultChecklist}
          loader="block"
        />
        <HeaderContainer>
          <PrimaryHeading>{t("libraryTemplate.checklist.title")}</PrimaryHeading>
          <div className="relative">
            <MainButton onClick={() => setAddPopup(!addPopup)}>
              <AddIcon />
              {t("button.add")}
            </MainButton>
            <PopOver open={addPopup} onClose={setAddPopup}>
              <AddFromTemplateLibrary
                heading={t("common.addFromTemplateLibraryTitle")}
                data={checklist
                  .filter((item) => !isEqual(item.default_tem, 1))
                  .map((item) => ({
                    title: item.title,
                    icon: <CheckListIcon fill="white" />,
                    onClick: () =>
                      addToDefaultChecklist({
                        id: item.id,
                        requested_by: parse(item.requested_from),
                      }),
                  }))}
                isLoading={loadingChecklistTemplate}
                emptyTemplate={{
                  label: t("libraryTemplate.checklist.title"),
                  link: `/${ModulePrefixes.TEMPLATE_LIBRARY}/checklists/add`,
                  linkLabel: t("libraryTemplate.checklist.title"),
                }}
              />
            </PopOver>
          </div>
        </HeaderContainer>
        <BodyContainer>
          <InputField
            placeholder={t("input.search.placeholder")}
            searchIcon={true}
            filter={true}
            value={searchText}
            setValue={(val) => setSearchText(val)}
            tags={tags ?? []}
            onApplyFilter={applyFilter}
            applySorting={applySorting}
            filterData={filterData}
            showLibrarySource={false}
          />
          <FiltersList filterData={filterData} updateFilter={applyFilter} />
          {loadingDefaultChecklists ? (
            <LoadingListCards />
          ) : searchResult.length === 0 ? (
            <TemplateNotFound
              label={t("libraryTemplate.checklist.title")}
              onClick={() => setAddPopup(true)}
              linkLabel={t("libraryTemplate.checklist.title")}
            />
          ) : (
            Children.toArray(
              searchResult.map((checklist) => {
                return (
                  <ListCard
                    editIcon={edit_templates}
                    link={
                      edit_templates ? `/${ModulePrefixes.SITE_TEMPLATE}/library-item/checklists/${checklist.id}` : ""
                    }
                    strictLink={true}
                    data={checklist.title}
                    onCross={() => removeFromDefaultChecklist(checklist.id)}
                    secondaryData={
                      isEqual(checklist.system_status, 1) ? t("module.sourceType.system") : ""
                    }
                    icon={
                      <ListIconWrapper>
                        <CheckListIcon fill="white" />
                      </ListIconWrapper>
                    }
                    deleteIcon
                    crossIcon={remove_items_from_site_template}
                    showModalonCross
                    deleteTitle={t("siteSetting.libraryTemplate.removeTemplate.title")}
                    deleteButtonText={t("siteSetting.libraryTemplate.removeTemplate.button")}
                    deleteMessage={t("siteSetting.libraryTemplate.removeTemplate.message")}
                  />
                );
              })
            )
          )}
        </BodyContainer>
      </RightMainContainer>
    </>
  );
}
